import { Navigate, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/Button";
import { Logo } from "../ui/Logo";
import { Loading } from "../ui/Spinner";
import { useAuth } from "./AuthProvider";
import { isInvitationReturn } from "../features/invitation/invitation";
import { safeReturnTo, signInPath, wasSignedIn } from "./realms";

function useCurrentPath(): string {
  return useRouterState({ select: (s) => s.location.href });
}

/** Renders its children only when signed in to the provider's realm; otherwise starts or offers sign-in. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, realm, signIn } = useAuth();
  const here = useCurrentPath();
  const resume = status === "signedOut" && wasSignedIn(realm);
  const { t } = useTranslation();

  useEffect(() => {
    if (resume) void signIn(here);
  }, [resume, signIn, here]);

  if (status === "loading" || resume) return <Loading label={resume ? t("signIn.redirecting") : undefined} />;
  if (status === "signedIn") return <>{children}</>;
  // While the router moves to the sign-in page this component renders once more with the new location; it must
  // not redirect again from there (the return path would nest itself).
  if (here.startsWith(signInPath(realm))) return null;
  return (
    <Navigate
      to={signInPath(realm)}
      search={{ returnTo: here, ...(status === "expired" ? { reason: "expired" } : {}) }}
      replace
    />
  );
}

export type SignInSearch = { returnTo?: string; reason?: string };

export function validateSignInSearch(search: Record<string, unknown>): SignInSearch {
  return {
    returnTo: typeof search.returnTo === "string" ? search.returnTo : undefined,
    reason: typeof search.reason === "string" ? search.reason : undefined,
  };
}

export function SignInPage({ search }: { search: SignInSearch }) {
  const { t } = useTranslation();
  const { realm, status, signIn } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (status === "signedIn") void navigate({ to: safeReturnTo(search.returnTo, realm), replace: true });
  }, [status, navigate, search.returnTo, realm]);

  return (
    <div className="flex min-h-full flex-col">
      <main className="flex flex-1 items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-sm rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="mb-6 flex justify-center">
            <Logo size={112} />
          </div>
          <h1 className="text-xl font-semibold text-brand">
            {realm === "ops" ? t("signIn.consoleTitle") : t("signIn.title")}
          </h1>
          {search.reason === "expired" && (
            <p role="alert" className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-950">
              {t("signIn.expired")}
            </p>
          )}
          <Button
            className="mt-6 w-full"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void signIn(search.returnTo).catch(() => setBusy(false));
            }}
          >
            {busy ? t("signIn.redirecting") : t("signIn.button")}
          </Button>
          {realm === "customers" && isInvitationReturn(search.returnTo) && (
            <p className="mt-4 rounded-md bg-blue-50 p-3 text-sm text-blue-950">{t("signIn.invitationHint")}</p>
          )}
          <p className="mt-4 text-xs text-gray-700">{t("signIn.passkeyHint")}</p>
        </div>
      </main>
    </div>
  );
}

// StrictMode runs effects twice in development; the code in the URL can be redeemed only once.
const callbacks = new Map<string, Promise<string>>();

export function CallbackPage() {
  const { t } = useTranslation();
  const { completeSignIn, realm } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const key = window.location.href;
    let p = callbacks.get(key);
    if (!p) {
      p = completeSignIn();
      callbacks.set(key, p);
    }
    p.then(
      (to) => void navigate({ to, replace: true }),
      (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
    );
  }, [completeSignIn, navigate]);

  if (!error) return <Loading />;
  return (
    <main className="mx-auto max-w-md p-8">
      <p role="alert" className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-900">
        {t("signIn.failed", { message: error })}
      </p>
      <a className="mt-4 inline-block text-sm text-brand underline" href={signInPath(realm)}>
        {t("signIn.button")}
      </a>
    </main>
  );
}
